import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { FormattedPhoneNumber } from "@/Common/Models/Portal/Patient/FormattedPhoneNumber";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import styles from "./PatientInfoBanner.module.scss";

import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPatientInfoBannerProps {
    patient: Patient;
    onPatientNameClick?: (name: string) => void;
    onHealthcardNumberClick?: (healthcardNumber: string) => void;
    onPatientPhoneNumberClick?: (phone: PhoneNumber) => void;
}

function renderEmails(emails: Email[]): JSX.Element {
    if (!emails || emails.length === 0)
        return null;

    let emailList = emails.map(x => (
        <li key={x.ContactPoint_id}>
            {x.Address}
        </li>
    ));

    return <ul className={styles.phoneList}>{emailList}</ul>;
}

export function PatientInfoBanner(props: Readonly<IPatientInfoBannerProps>): JSX.Element {
    const validationHelper: ValidationHelper = new ValidationHelper();

    const { getText } = useTranslation();

    const prefix = cacheStore.GetLocalizedValueByLookupId(cacheStore.GetLookupById(props.patient.IndividualResponse.PrefixLookupId)?.LookupID) ?? "";

    const dateOfBirth = props.patient.IndividualResponse.DateOfBirth;
    const dateOfBirthString = dateOfBirth
        ? LuxonDateTimeParser.parse(dateOfBirth)?.toFormat("yyyy/LL/dd")
        : "";

    const ageString = dateOfBirth ? PatientHelper.getFormattedAge(new Date(dateOfBirthString)) : null;

    let portalButton: JSX.Element = null;

    if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
        const portalButtonProps = PortalHelper.getPortalButtonPropsForRegistrationState(props.patient.IndividualResponse.PortalSyncState);

        portalButton = portalButtonProps && (
            <div style={{ alignSelf: "center" }}>
                <PortalStatusIcon
                    {...portalButtonProps}
                />
            </div>
        );
    }

    return (
        <div className={styles.patientView}>
            <div className={styles.patientHeader}>
                <div className={styles.logo}>
                    <img src={img_p_banner_icon_100} alt="MYLE" />
                    {portalButton}
                </div>
                <div className={styles.demographics}>
                    <div> {prefix} <a className="name" onClick={() => props.onPatientNameClick(`${props.patient.IndividualResponse.FirstName} ${props.patient.IndividualResponse.LastName}`)}> {NameHelper.formatFullNameForHasName(props.patient.IndividualResponse, NameHelperFormat.UpperLastNameFirst)}</a></div>
                    <div>
                        <span>{getText("HealthCard")}</span> <a onClick={() => props.onHealthcardNumberClick(props.patient.IndividualResponse.NAM)}> {props.patient.IndividualResponse.NAM} </a>
                    </div>
                    <div>
                        <span>{getText("DateofBirthLabel")}</span> <strong>{dateOfBirthString} {ageString ? `(${ageString})` : ""} </strong>
                    </div>
                </div>
            </div>

            <div className="margin-xs-bottom">
                <h5 className={styles.contactSectionTitle}>{getText("PhoneNumberLabel")}</h5>
                <ul className={styles.phoneList}>
                    {props.patient.Phones.map((phone: PhoneNumber, i: number) => {
                        let formattedPhoneNumber: FormattedPhoneNumber = PatientBannerHelper.getFormattedPhoneNumber(phone, validationHelper);
                        return (
                            <li key={i + "__npl"}>
                                <div> <span className="myle-icon-16 phone" /> <a className="bold" onClick={() => props.onPatientPhoneNumberClick(phone)}>{formattedPhoneNumber.FormattedPhoneNumber}</a> <span>({formattedPhoneNumber.ContactType} - {formattedPhoneNumber.PhoneType}) </span></div>
                            </li>
                        );
                    })}
                </ul>

                <h5 className={styles.contactSectionTitle}>{getText("AddressLabel")}</h5>
                <ul className={styles.phoneList}>
                    {props.patient.Addresses.map((a: Address) => {
                        let addressDisplay = Address.GetAddressDisplayModel(a);
                        return (
                            <li key={a.ContactPoint_id}>
                                <div><strong>{addressDisplay.streetAddress}</strong></div>
                                <div>
                                    <strong>{addressDisplay.city}</strong>
                                    {!IsNullOrWhiteSpace(addressDisplay.state) &&
                                        <strong>, {addressDisplay.state}</strong>
                                    }
                                </div>
                                <div><strong>{addressDisplay.zipCode}</strong></div>
                            </li>
                        );
                    })}
                </ul>

                <h5 className={styles.contactSectionTitle}>{getText("EmailLabel")}</h5>
                {renderEmails(props.patient.Emails)}
            </div>
        </div>
    );
}
